import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { invalidateCrmCache } from "@/lib/crmApi";
import { nastavZemiTenanta, zapomenZemiTenanta } from "@/lib/tenantMena";
/* Paměť odkazů na doporučení (contactId → odkaz) — bez závislostí, takže
 * se sem nenačte qrcode ani nic dalšího z lib/doporuceni. */
import { zapomenOdkazyNaDoporuceni } from "@/lib/doporuceni/pametOdkazu";
import { smazatApiCache } from "@/lib/smazatApiCache";

const AuthContext = createContext(null);

// Keep-alive ping interval — backend container sleeps after ~5min idle on
// Emergent free/cheap tiers. We ping every 3 min while the user has the app
// open so the container stays warm during their work session (critical
// during in-person presentations at company demos).
const KEEPALIVE_MS = 3 * 60 * 1000;

// Login retry budget: total ≈ 90s. Cold-start on Emergent can take 60+ s in
// the worst case, so we keep trying long enough that the advisor sees only
// a polite "server is waking up" message — never a raw 520.
const LOGIN_RETRY_MAX = 10;
const LOGIN_RETRY_DELAYS = [1000, 1500, 2500, 4000, 6000, 8000, 10000, 12000, 15000, 20000];
//                            1.0   2.5   5.0    9.0   15.0  23.0  33.0   45.0   60.0   80.0 (cumulative)

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isColdStartError = (err) => {
  if (!err) return false;
  // No response (network/timeout/CORS) → likely cold start or gateway
  if (!err.response) return true;
  const s = err.response.status;
  // Cloudflare 5xx + standard gateway errors → origin not ready
  return s === 502 || s === 503 || s === 504 || s === 520 || s === 521 || s === 522 || s === 523 || s === 524;
};

// Aggressive backend wake-up: keep pinging until /api/ping returns OK.
// Runs at app boot AND on every login page render. Total budget ≈ 60 s.
// Resolves true when backend responds, false on total timeout.
export async function wakeBackend({ maxAttempts = 8, onTick } = {}) {
  const delays = [0, 1500, 3000, 5000, 8000, 10000, 12000, 15000];
  for (let i = 0; i < Math.min(maxAttempts, delays.length); i++) {
    if (delays[i] > 0) await sleep(delays[i]);
    try {
      await api.get("/api/ping", { timeout: 12000 });
      return true;
    } catch (e) {
      if (typeof onTick === "function") onTick(i + 1, maxAttempts);
    }
  }
  return false;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const keepAliveRef = useRef(null);

  const checkSession = useCallback(async () => {
    // Retry /auth/me a few times on 5xx — covers cold-start at app boot so
    // an already-logged-in user does not get bounced to login on refresh.
    const maxAttempts = 5;
    const delays = [800, 1500, 3000, 5000];
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const { data } = await api.get("/api/auth/me");
        setUser(data);
        // MĚNA ≠ JAZYK: země tenanta je jediný zdroj měny (lib/tenantMena.js).
        // Zapisuje se sem, protože LanguageProvider je v App.js NAD tímhle
        // providerem a nemůže si uživatele vzít hookem.
        nastavZemiTenanta(data?.country_code);
        setLoading(false);
        return;
      } catch (err) {
        if (!isColdStartError(err)) {
          // 401 → not logged in (normal). Don't retry.
          setUser(null);
          setLoading(false);
          return;
        }
        if (attempt < maxAttempts - 1) {
          await sleep(delays[attempt] || 5000);
        }
      }
    }
    // All retries exhausted → assume logged out (fail-safe)
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  // Boot-time wake: ping backend on first app load so the container is
  // ready by the time anything else queries it. Non-blocking.
  useEffect(() => {
    wakeBackend({ maxAttempts: 4 }).catch(() => {});
  }, []);

  // Keep-alive ping: while logged in, hit /api/ping every 3 min so the
  // backend container does not go to sleep.
  useEffect(() => {
    if (!user) {
      if (keepAliveRef.current) {
        clearInterval(keepAliveRef.current);
        keepAliveRef.current = null;
      }
      return;
    }
    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      api.get("/api/ping", { timeout: 8000 }).catch(() => {});
    };
    tick();
    keepAliveRef.current = setInterval(tick, KEEPALIVE_MS);
    return () => {
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    };
  }, [user]);

  const login = async (email, password, opts = {}) => {
    let lastErr = null;
    for (let attempt = 0; attempt < LOGIN_RETRY_MAX; attempt++) {
      try {
        const { data } = await api.post("/api/auth/login", { email, password }, { timeout: 20000 });
        // Čistý start: zahodit cache klientů předchozího uživatele (kdyby se
        // neodhlásil řádně) — jinak by nový účet krátce viděl cizí PII.
        try { localStorage.removeItem("klienti.cache"); invalidateCrmCache(); } catch { /* noop */ }
        setUser(data);
        // Země tenanta z odpovědi loginu. Když ji backend (starší build) ještě
        // neposílá, spadne to na CZ a dotáhne se z /auth/me — nikdy nesmí zůstat
        // viset země PŘEDCHOZÍHO poradce ze sdíleného počítače.
        nastavZemiTenanta(data?.country_code);
        if (!data?.country_code) checkSession().catch(() => {});
        return data;
      } catch (err) {
        lastErr = err;
        // Don't retry on auth errors (401/400/403) — credentials are wrong
        if (!isColdStartError(err)) throw err;
        if (typeof opts.onRetry === "function") {
          opts.onRetry(attempt + 1, LOGIN_RETRY_MAX);
        }
        if (attempt < LOGIN_RETRY_MAX - 1) {
          await sleep(LOGIN_RETRY_DELAYS[attempt] || 5000);
        }
      }
    }
    throw lastErr;
  };

  const register = async (email, password, name, accessCode) => {
    const { data } = await api.post("/api/auth/register", {
      email, password, name, access_code: accessCode
    });
    setUser(data);
    nastavZemiTenanta(data?.country_code);
    if (!data?.country_code) checkSession().catch(() => {});
    return data;
  };

  const logout = async () => {
    let serverOk = true;
    try { await api.post("/api/auth/logout"); } catch { serverOk = false; }
    // Bezpečnost/GDPR: lokální data klientů NESMÍ přežít odhlášení — na sdíleném
    // počítači by je jinak viděl další přihlášený poradce (klienti.cache = PII).
    try {
      localStorage.removeItem("klienti.cache");
      invalidateCrmCache();
    } catch { /* noop */ }
    // Země tenanta je taky součást session — na sdíleném počítači nesmí měna
    // jednoho poradce přetéct do účtu dalšího.
    zapomenZemiTenanta();
    // Odkazy na doporučení z paměti (až 10 min) patří přihlášenému poradci —
    // další přihlášený by jinak dostal QR/odkaz s tokenem předchozího.
    zapomenOdkazyNaDoporuceni();
    // Kopie odpovědí API (klienti, analýzy, přehledy týmu), které si service
    // worker drží pro výpadek sítě, taky nesmí přežít odhlášení.
    await smazatApiCache();
    setUser(null);
    if (!serverOk) {
      toast.warning("Odhlášení na serveru selhalo — session může být stále aktivní. Pro jistotu zavři prohlížeč.");
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
}
